import { ChevronDownIcon, Plus, Search, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v3';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent } from '@/components/shadcn/card.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Textarea } from '@/components/shadcn/textarea';
import { createSystemPending } from '@/modules/governmentIntake/api/shared';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component';
import { EMPTY_PROGRAM_CURRENT_SYSTEMS, type ProgramSystem, useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context';
import { usePublicSystemCatalog } from '@/modules/governmentIntake/hooks/use-public-system-catalog';
import { notifyUser } from '@/store/Toast';

const addSystemModalSchema = z.object({
  name: z.string().min(1, 'System name is required'),
  vendor: z.string().optional(),
  notes: z.string().optional(),
});

type AddSystemFormData = z.infer<typeof addSystemModalSchema>;

const DEFAULT_ADD_FORM: AddSystemFormData = {
  name: '',
  vendor: '',
  notes: '',
};

export default function PropertiesSubCurrentSystem({ onValidationChange }: ValidationChange) {
  const { data, updatePropertiesSubSection } = useIntakeForm();
  const currentSystemsData = data.properties?.operationalSystems ?? EMPTY_PROGRAM_CURRENT_SYSTEMS;
  const selectedSystems = currentSystemsData.systems;
  const { options: systemCatalogOptions } = usePublicSystemCatalog();

  const dispatch = useDispatch();

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const [searchQuery, setSearchQuery] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<AddSystemFormData>(DEFAULT_ADD_FORM);
  const [addFormErrors, setAddFormErrors] = useState<Record<string, string[]>>({});
  const systemNameId = useId();
  const systemVendorId = useId();
  const systemNotesId = useId();
  const isSystemNameValid = addFormData.name.trim().length > 0;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updateSystems = (newSystems: ProgramSystem[]) => {
    updatePropertiesSubSection('operationalSystems', { systems: newSystems });
  };

  const filteredSystems = systemCatalogOptions
    .filter(
      (s) =>
        !selectedSystems.some((sel) => sel.id === s.id) &&
        (searchQuery === '' ||
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (s.vendor?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleSelectSystem = (system: ProgramSystem) => {
    updateSystems([...selectedSystems, system]);
    setSearchQuery('');
  };

  const handleRemoveSystem = (id: string) => {
    updateSystems(selectedSystems.filter((s) => s.id !== id));
  };

  const handleUpdateSystemNotes = (id: string, notes: string) => {
    updateSystems(selectedSystems.map((s) => (s.id === id ? { ...s, notes } : s)));
  };

  const handleAddSystemNotListed = () => {
    setAddModalOpen(true);
    setAddFormData(DEFAULT_ADD_FORM);
    setAddFormErrors({});
  };

  const handleAddModalSubmit = async () => {
    const result = addSystemModalSchema.safeParse(addFormData);
    if (!result.success) {
      setAddFormErrors(result.error.flatten().fieldErrors);
      return;
    }

    try {
      await createSystemPending({
        submittedName: addFormData.name.trim(),
        submittedVendor: addFormData.vendor?.trim() || undefined,
        submittedNotes: addFormData.notes?.trim() || undefined,
      });
    } catch (error) {
      console.error('Failed to submit pending system to backend.', error);
    }

    const nextCustomNumber =
      selectedSystems.reduce((max, system) => {
        const matched = system.id.match(/^custom-system-(\d+)$/);
        if (!matched) {
          return max;
        }
        const parsed = parseInt(matched[1], 10);
        return Number.isNaN(parsed) ? max : Math.max(max, parsed);
      }, 1000) + 1;

    const customSystem: ProgramSystem = {
      id: `custom-system-${nextCustomNumber}`,
      name: addFormData.name,
      vendor: addFormData.vendor || '',
      notes: addFormData.notes || '',
    };

    updateSystems([...selectedSystems, customSystem]);
    setAddModalOpen(false);
    setAddFormData(DEFAULT_ADD_FORM);
    setAddFormErrors({});

    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: 'System submitted for review. You can continue your intake.',
        toastType: 'success',
      })
    );
  };

  const handleAddModalCancel = () => {
    setAddModalOpen(false);
    setAddFormData(DEFAULT_ADD_FORM);
    setAddFormErrors({});
  };

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <header className='shrink-0 pt-4'>
        <HeaderAndSub
          title='Current Systems'
          subTitle='List all systems used today, even if they overlap with other departments. This helps identify integration and compatibility considerations.'
        />
      </header>

      <section className='flex-1 overflow-y-auto pb-4'>
        <div className='space-y-6 pt-8'>
          <div>
            <h3 className='text-xl font-semibold text-foreground mb-4'>Current Systems</h3>
            <p className='flex w-full text-sm text-muted-foreground mb-4'>
              Select the systems and tools your department uses for programs.
            </p>

            {/* Searchable dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  className='flex h-10 w-full items-center justify-between rounded-md border border-input bg-gray-100 hover:bg-gray-100 px-3 py-2 text-sm'
                >
                  <p className='text-muted-foreground bg-transparent'>Search and select systems/tools...</p>
                  <ChevronDownIcon className='size-4 shrink-0 opacity-50 text-black' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='start'
                className='w-(--radix-popper-anchor-width) max-h-120 overflow-auto p-0
                                data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0
                                data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-2
                                data-[state=closed]:slide-out-to-top-2 duration-200 ease-out hover:cursor-pointer'
              >
                <div className='border-b p-2' onPointerDown={(e) => e.stopPropagation()}>
                  <div className='flex items-center gap-2'>
                    <Search className='size-4 text-muted-foreground pointer-events-none' />
                    <Input
                      placeholder='Search systems...'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        outline: 'none',
                        borderColor: '#FFF',
                        accentColor: '#FFF',
                        boxShadow: 'none',
                      }}
                      className='h-8 border-0 bg-transparent'
                    />
                  </div>
                </div>
                <div className='overflow-auto p-1 hover:cursor-pointer'>
                  {filteredSystems.length === 0 ? (
                    <div className='py-6 text-center text-sm text-muted-foreground'>No systems found</div>
                  ) : (
                    filteredSystems.map((system) => (
                      <DropdownMenuItem
                        className='hover:cursor-pointer hover:bg-gray-100 rounded-lg'
                        key={system.id}
                        onSelect={() => handleSelectSystem(system)}
                      >
                        <div className='flex w-full flex-col p-2 space-y-1'>
                          <Label className='flex w-full hover:cursor-pointer font-medium text-black '>{system.name}</Label>
                          <Label className='flex w-full hover:cursor-pointer text-gray-500 text-xs'>{system.vendor}</Label>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add system not listed */}
            <Button
              type='button'
              onClick={handleAddSystemNotListed}
              className='mt-3 inline-flex items-center text-sm bg-transparent hover:bg-transparent hover:underline font-medium text-green-600'
            >
              <Plus className='size-4' />
              Add system not listed
            </Button>

            <div className='flex justify-center items-center mt-6 py-6'>
              {selectedSystems.length === 0 ? (
                <p className='flex w-full min-h-20 border-3 border-dotted rounded-lg justify-center items-center text-center text-muted-foreground'>
                  No systems selected yet
                </p>
              ) : (
                <div className='flex flex-col w-full gap-2'>
                  {selectedSystems.map((system) => (
                    <Card className='shadow-none border-gray-300 border-2' key={system.id}>
                      <CardContent>
                        <div className='flex w-full justify-between mb-1'>
                          <span className='text-base text-black'>
                            {system.name}
                            {system.id.includes('custom-system') && (
                              <span className='inline-block ml-6 px-2 py-1 text-xs bg-yellow-50 border border-orange-100 text-yellow-700 rounded-xl'>
                                Pending review
                              </span>
                            )}
                          </span>
                          <Button className='h-8 bg-white hover:bg-gray-50 text-black' onClick={() => handleRemoveSystem(system.id)}>
                            <X className='h-6 hover:text-black' />
                          </Button>
                        </div>
                        <Label className='block text-sm text-gray-800 my-2'>Notes (optional)</Label>
                        <Input
                          type='text'
                          className='w-full h-10 shadow-none'
                          placeholder='Add any notes about this system...'
                          value={system.notes ?? ''}
                          onChange={(e) => handleUpdateSystemNotes(system.id, e.target.value)}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Add system not listed modal */}
      <Dialog open={addModalOpen} onOpenChange={(open) => !open && handleAddModalCancel()}>
        <DialogContent className='p-0 flex flex-col rounded-xl'>
          <div className='flex items-start justify-between px-6 pt-6'>
            <DialogHeader className='p-0 space-y-1'>
              <DialogTitle className='text-black font-semibold'>Add a system not listed.</DialogTitle>
              <DialogDescription>
                This will be reviewed and standardized by the SEIN team. Once approved, it will become available for selection.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className='grid gap-4 px-6 py-4'>
            <div className='space-y-2'>
              <Label htmlFor={systemNameId}>
                System name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id={systemNameId}
                placeholder='Enter system name.'
                value={addFormData.name}
                onChange={(e) => setAddFormData((p) => ({ ...p, name: e.target.value }))}
                className={addFormErrors.name ? 'border-destructive' : ''}
              />
              {addFormErrors.name && <p className='text-xs text-destructive'>{addFormErrors.name[0]}</p>}
            </div>

            <div className='space-y-2'>
              <Label htmlFor={systemVendorId}>Vendor</Label>
              <Input
                id={systemVendorId}
                placeholder='Vendor name (optional).'
                value={addFormData.vendor}
                onChange={(e) => setAddFormData((p) => ({ ...p, vendor: e.target.value }))}
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor={systemNotesId}>Notes / context</Label>
              <Textarea
                id={systemNotesId}
                rows={3}
                className='resize-none'
                placeholder='Any additional context (optional).'
                value={addFormData.notes}
                onChange={(e) => setAddFormData((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter className='px-6 pb-6'>
            <Button className='bg-white hover:bg-gray-100 text-black border border-b-2 border-gray-300 ' onClick={handleAddModalCancel}>
              Cancel
            </Button>
            <Button
              type='button'
              disabled={!isSystemNameValid}
              className='bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
              onClick={handleAddModalSubmit}
            >
              Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
